import * as Yup from 'yup';
import {getSelectItemLabel, ISelectItem} from '~/components/Controls/Select';
import {ID, IModel} from '~/lib/model';
import {BidderCappingSchema, defaultCapping, IBidderCapping} from '~/modules/Bidders/models/BidderCapping';

export enum Platform {
    ANDROID = 'android',
    IOS = 'ios',
    AMAZON = 'amazon'
}

export const platforms = Object.keys(Platform).map(key => Platform[key]);

export enum AuctionType {
    FIRST = 1,
    SECOND = 2
}

// Mirrors the Encoding enum in common models; absent value is resolved from protocol by the backend
export enum Encoding {
    IDENTITY = 'identity',
    GZIP = 'gzip',
    ZSTD = 'zstd'
}

export const bidderEncodings = Object.keys(Encoding).map(key => Encoding[key]);

export type TagIdRequirement = 'no_filtering' | 'original_only' | 'any_non_empty_tag_id';

export const tagIdRequirementLabels: Record<TagIdRequirement, string> = {
    no_filtering: 'No filtering',
    original_only: 'Original TagID only',
    any_non_empty_tag_id: 'Any non-empty TagID'
};

export const tagIdRequirementItems = (Object.keys(tagIdRequirementLabels) as TagIdRequirement[])
    .map(value => ({label: tagIdRequirementLabels[value], value}));

export type GpidRequirement = 'no_filtering' | 'original_only' | 'any_non_empty_gpid';

export const gpidRequirementLabels: Record<GpidRequirement, string> = {
    no_filtering: 'No filtering',
    original_only: 'Original GPID only',
    any_non_empty_gpid: 'Any non-empty GPID'
};

export const gpidRequirementItems = (Object.keys(gpidRequirementLabels) as GpidRequirement[])
    .map(value => ({label: gpidRequirementLabels[value], value}));

export type SupplyPlacement = 'fullscreen' | 'rewarded' | 'small_banner' | 'mrec' | 'native';
export const CREATIVE_FORMATS = {banner: 1, video: 2, native: 4} as const;
export type CreativeFormat = typeof CREATIVE_FORMATS[keyof typeof CREATIVE_FORMATS];
export type PlacementFormatsTable = Partial<Record<SupplyPlacement, CreativeFormat[]>>;

export const supplyPlacements: SupplyPlacement[] = ['fullscreen', 'rewarded', 'small_banner', 'mrec', 'native'];
export const creativeFormats: CreativeFormat[] = [CREATIVE_FORMATS.banner, CREATIVE_FORMATS.video, CREATIVE_FORMATS.native];

export const AVAILABLE_FORMATS: Record<SupplyPlacement, CreativeFormat[]> = {
    fullscreen: creativeFormats,
    rewarded: creativeFormats,
    small_banner: [CREATIVE_FORMATS.banner, CREATIVE_FORMATS.native],
    mrec: creativeFormats,
    native: [CREATIVE_FORMATS.native]
};

export const supplyPlacementLabels: Record<SupplyPlacement, string> = {
    fullscreen: 'Fullscreen',
    rewarded: 'Rewarded',
    small_banner: 'Small banner',
    mrec: 'MREC',
    native: 'Native'
};

export const creativeFormatLabels: Record<CreativeFormat, string> = {
    [CREATIVE_FORMATS.banner]: 'Banner',
    [CREATIVE_FORMATS.video]: 'Video',
    [CREATIVE_FORMATS.native]: 'Native'
};

export const hasAnyFormat = (table: PlacementFormatsTable) => Object.values(table).some(formats => !!formats?.length);

export interface IBidder extends IModel {
    check: boolean;
    agencyId: ID;
    title: string;
    tags: string[];
    endpoint: string;
    coppaFlag: boolean;
    excludeFromNonInBannerAuction: boolean;
    countries?: string[];
    platforms: Platform[];
    maxRpm: number;
    adControl: boolean;
    includedSellers: number[];
    protocol: string;
    encoding?: Encoding | null;
    auctionType?: AuctionType;
    delayedNotification: boolean;
    wseat?: string[] | string | null;
    bseat?: string[] | string | null;
    minBidFloor: number | string | null;
    maxBidFloor: number | string | null;
    floorType: string;
    noFloor: boolean;
    skAnIdCheck: boolean;
    skAnId: string | null;
    dc?: string[];
    spendCapping: IBidderCapping;
    impExpirationTime?: number;
    status: string;
    pauseReasons?: string[];
    pausedUntil?: string | null;
    mockBidResponse?: string;
    useAdMergeType?: boolean;
    auctionCategory: 'performance' | 'branded';
    clientNurl: boolean;
    clientLurl: boolean;
    passCalloutRound: boolean;
    tagIdRequirement: TagIdRequirement;
    gpidRequirement: GpidRequirement;
    healthyAvgPrice?: number | null;
    placementFormats?: PlacementFormatsTable | null;
    multiFormatRequest: boolean;
}

export const BidderSchema = (isAdmin: boolean) => Yup.object().shape<IBidder>({
    check: Yup.boolean().nullable(),
    id: Yup.number().nullable(),
    agencyId: Yup.number().nullable().required(),
    title: Yup.string().required(),
    tags: Yup.array<string>().nullable(),
    endpoint: Yup.string().required(),
    coppaFlag: Yup.boolean(),
    excludeFromNonInBannerAuction: Yup.boolean(),
    platforms: Yup.array(),
    maxRpm: isAdmin ? Yup.number().min(0).required('is required') : Yup.number().min(0).max(2500).required('is required'),
    adControl: Yup.boolean(),
    includedSellers: Yup.array(),
    protocol: Yup.string(),
    encoding: Yup.string().nullable(),
    delayedNotification: Yup.boolean(),
    wseat: Yup.array<string>().nullable(),
    bseat: Yup.array<string>().nullable(),
    minBidFloor: Yup.mixed().nullable(),
    maxBidFloor: Yup.mixed().nullable(),
    floorType: Yup.string().required(),
    noFloor: Yup.boolean().nullable(),
    skAnIdCheck: Yup.boolean().nullable(),
    skAnId: Yup.string().nullable(),
    dc: Yup.array<string>().nullable(),
    spendCapping: BidderCappingSchema,
    impExpirationTime: Yup.number().nullable(),
    status: Yup.string(),
    pauseReasons: Yup.array<string>().nullable(),
    pausedUntil: Yup.string().nullable(),
    mockBidResponse: Yup.string().nullable(),
    useAdMergeType: Yup.boolean().nullable(),
    auctionCategory: Yup.string().oneOf(['performance', 'branded']),
    clientNurl: Yup.boolean(),
    clientLurl: Yup.boolean(),
    passCalloutRound: Yup.boolean(),
    tagIdRequirement: Yup.string().oneOf(['no_filtering', 'original_only', 'any_non_empty_tag_id']),
    gpidRequirement: Yup.string().oneOf(['no_filtering', 'original_only', 'any_non_empty_gpid']),
    healthyAvgPrice: Yup.number().nullable().positive(),
    placementFormats: Yup.mixed().nullable(),
    multiFormatRequest: Yup.boolean()
});

export const defaultBidder: IBidder = {
    check: false,
    id: null,
    agencyId: null,
    title: '',
    tags: [],
    endpoint: '',
    coppaFlag: false,
    excludeFromNonInBannerAuction: false,
    countries: [],
    platforms: [],
    maxRpm: 0,
    adControl: false,
    includedSellers: [],
    protocol: '',
    encoding: null,
    auctionType: AuctionType.FIRST,
    delayedNotification: false,
    wseat: '',
    bseat: '',
    minBidFloor: null,
    maxBidFloor: null,
    floorType: 'default',
    noFloor: false,
    skAnIdCheck: false,
    skAnId: null,
    dc: [],
    spendCapping: defaultCapping,
    impExpirationTime: null,
    status: 'enabled',
    pauseReasons: [],
    pausedUntil: null,
    mockBidResponse: null,
    useAdMergeType: false,
    clientNurl: false,
    clientLurl: false,
    passCalloutRound: false,
    tagIdRequirement: 'no_filtering',
    gpidRequirement: 'no_filtering',
    healthyAvgPrice: null,
    placementFormats: null,
    multiFormatRequest: false
};
